import React, { useState, useEffect } from 'react';
import API from '../api';
import '../css/AccountSettings.css';

function AccountSettingsPage({ user, setPath }) {
  const [profile, setProfile] = useState({ first_name: '', last_name: '', email: '' });
  const [passwordForm, setPasswordForm] = useState({ old_password: '', new_password: '', confirm_password: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [feedback, setFeedback] = useState({ type: null, message: null });
  const [showPassword, setShowPassword] = useState({ old: false, next: false, confirm: false });
  const [verification, setVerification] = useState(() => ({
    status: user?.verification_status || 'LOADING',
    verified: user?.verified === true,
  }));

  // Populate profile when user data is available
  useEffect(() => {
    if (user) {
      setProfile({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        email: user.email || '',
      });
    }
  }, [user]);

  useEffect(() => {
    API.get('/verification/')
      .then((res) => {
        setVerification({
          status: res.data?.status || 'NOT_SUBMITTED',
          verified: res.data?.verified === true,
        });
      })
      .catch((err) => {
        console.error(err);
        setVerification((current) => (
          current.status === 'LOADING'
            ? { status: 'NOT_SUBMITTED', verified: false }
            : current
        ));
      });
  }, []);

  const computeInitials = (firstName, lastName) => {
    const a = (firstName || '').trim().charAt(0).toUpperCase();
    const b = (lastName || '').trim().charAt(0).toUpperCase();
    return (a + b) || 'U';
  };

  const handlePasswordInput = (e) => {
    setPasswordForm({ ...passwordForm, [e.target.name]: e.target.value });
  };

  const goToVerification = () => {
    window.history.pushState(null, '', '/verification?returnTo=/account-settings&action=account-settings');
    setPath('/verification');
  };

  const validatePasswordForm = () => {
    if (!passwordForm.old_password || !passwordForm.new_password || !passwordForm.confirm_password) {
      return 'Veuillez remplir tous les champs.';
    }
    if (passwordForm.new_password.length < 8) {
      return 'Le nouveau mot de passe doit contenir au moins 8 caractères.';
    }
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      return 'Les mots de passe ne correspondent pas.';
    }
    return null;
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setFeedback({ type: null, message: null });
    const error = validatePasswordForm();
    if (error) {
      setFeedback({ type: 'error', message: error });
      return;
    }
    try {
      setIsSubmitting(true);
      await API.post('/change-password/', passwordForm);
      setFeedback({ type: 'success', message: 'Mot de passe changé avec succès.' });
      setPasswordForm({ old_password: '', new_password: '', confirm_password: '' });
    } catch (err) {
      // Try to surface server error if available
      const serverMessage = err?.response?.data?.detail || 'Une erreur est survenue lors du changement de mot de passe.';
      setFeedback({ type: 'error', message: serverMessage });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="settingsPro-section">
      <header className="settingsPro-header">
        <div>
          <h2 className="settingsPro-title">Paramètres du compte</h2>
          <p className="settingsPro-subtitle">Gérez vos informations et sécurisez votre compte</p>
        </div>
      </header>

      {feedback.message && (
        <div
          className={`settingsPro-alert ${feedback.type === 'success' ? 'settingsPro-alert--success' : 'settingsPro-alert--error'}`}
          role="alert"
        >
          {feedback.message}
        </div>
      )}

      <div className="settingsPro-grid">
        {/* Profile card */}
        <article className="settingsPro-card settingsPro-profileCard">
          <div className="settingsPro-profileHeader">
            <div className="settingsPro-avatar" aria-hidden>
              {computeInitials(profile.first_name, profile.last_name)}
            </div>
            <div>
              <div className="settingsPro-profileNameRow">
                <div className="settingsPro-profileName">{`${profile.first_name} ${profile.last_name}`.trim() || 'Utilisateur'}</div>
              </div>
              <div className="settingsPro-profileEmail">{profile.email || '—'}</div>
            </div>
          </div>
          {verification.status !== 'LOADING' && (
            <div className={`settingsPro-verificationBox ${verification.verified ? 'settingsPro-verificationBox--verified' : ''}`}>
              {verification.verified ? (
                <>
                  <div className="settingsPro-verificationIcon">✓</div>
                  <div>
                    <h3>Compte vérifié</h3>
                  </div>
                </>
              ) : (
                <>
                  <div className="settingsPro-verificationIcon">!</div>
                  <div className="settingsPro-verificationContent">
                    <h3>Vérification du compte</h3>
                    <p>
                      {verification.status === 'PENDING'
                        ? "Votre vérification est en cours d'examen par un administrateur."
                        : "Vérifiez votre compte pour débloquer tous les avantages Adabo Cash."}
                    </p>
                    {verification.status !== 'PENDING' && (
                      <button type="button" className="settingsPro-verifyBtn" onClick={goToVerification}>
                        Vérifier mon compte
                      </button>
                    )}
                  </div>
                </>
              )}
            </div>
          )}
          <div className="settingsPro-profileBody">
            <div className="settingsPro-field">
              <label>Prénom</label>
              <input value={profile.first_name} readOnly disabled />
            </div>
            <div className="settingsPro-field">
              <label>Nom</label>
              <input value={profile.last_name} readOnly disabled />
            </div>
            <div className="settingsPro-field">
              <label>Email</label>
              <input type="email" value={profile.email} readOnly disabled />
            </div>
          </div>
        </article>

        {/* Password change card */}
        <article className="settingsPro-card">
          <h3 className="settingsPro-cardTitle">Sécurité du compte</h3>
          <form onSubmit={handleChangePassword} className="settingsPro-form" noValidate>
            <div className="settingsPro-field">
              <label htmlFor="old_password">Mot de passe actuel</label>
              <div className="settingsPro-inputWrap">
                <input
                  id="old_password"
                  name="old_password"
                  type={showPassword.old ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={passwordForm.old_password}
                  onChange={handlePasswordInput}
                  required
                />
                <button
                  type="button"
                  className="settingsPro-visibilityBtn"
                  aria-label={showPassword.old ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                  onClick={() => setShowPassword({ ...showPassword, old: !showPassword.old })}
                >
                  {showPassword.old ? '🙈' : '👁️'}
                </button>
              </div>
            </div>

            <div className="settingsPro-field">
              <label htmlFor="new_password">Nouveau mot de passe</label>
              <div className="settingsPro-inputWrap">
                <input
                  id="new_password"
                  name="new_password"
                  type={showPassword.next ? 'text' : 'password'}
                  placeholder="Au moins 8 caractères"
                  value={passwordForm.new_password}
                  onChange={handlePasswordInput}
                  required
                  minLength={8}
                />
                <button
                  type="button"
                  className="settingsPro-visibilityBtn"
                  aria-label={showPassword.next ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                  onClick={() => setShowPassword({ ...showPassword, next: !showPassword.next })}
                >
                  {showPassword.next ? '🙈' : '👁️'}
                </button>
              </div>
            </div>

            <div className="settingsPro-field">
              <label htmlFor="confirm_password">Confirmer le nouveau mot de passe</label>
              <div className="settingsPro-inputWrap">
                <input
                  id="confirm_password"
                  name="confirm_password"
                  type={showPassword.confirm ? 'text' : 'password'}
                  placeholder="Répétez le mot de passe"
                  value={passwordForm.confirm_password}
                  onChange={handlePasswordInput}
                  required
                  minLength={8}
                />
                <button
                  type="button"
                  className="settingsPro-visibilityBtn"
                  aria-label={showPassword.confirm ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                  onClick={() => setShowPassword({ ...showPassword, confirm: !showPassword.confirm })}
                >
                  {showPassword.confirm ? '🙈' : '👁️'}
                </button>
              </div>
            </div>

            <div className="settingsPro-actions">
              <button type="submit" className="settingsPro-primaryBtn" disabled={isSubmitting}>
                {isSubmitting ? 'En cours…' : 'Changer le mot de passe'}
              </button>
            </div>
          </form>
        </article>

      </div>
    </section>
  );
}

export default AccountSettingsPage;